// Right-Now relevance scoring for photo-coaching scenarios. The tab
// surfaces top 2-3 scored cards by default; the user can switch to
// browse-all. Scoring is deterministic + cheap so it can run on every
// render of the tab without memo gymnastics.
//
// Inputs:
//   • scenarios     — array from photo-coaching.json
//   • activePlayerId — current player session
//   • now            — Date (real or test-mode mock)
//   • coords         — { lat, lng } | null (gps / mock / cached)
//   • excursions     — array from excursions.json (for trigger-time lookup)
//   • sunsetMs       — sunset timestamp (ms) for the day, used for
//                       golden-hour boost. Optional; falls back to
//                       crude time-of-day heuristics if missing.
//
// Score components (max ~330):
//   • Player match:                    +50
//   • Trigger time within ±90 min:     +100  (peak window)
//   • Trigger time within ±240 min:    +30   (wider window)
//   • Golden hour proximity:           +60   (≤60 min before sunset)
//   • Geo proximity (within 5 km):     +60
//   • Geo proximity (within 15 km):    +20
//
// Top 3 by score is the "Right Now" surface. Ties broken by trigger
// proximity (sooner wins).

const ACTIVITY_COORDS = {
  // Whale watch all variants → Auke Bay / Statter Harbor
  'Whale watch': { lat: 58.3811, lng: -134.6420 },
  'Whale watch — boat documentary': { lat: 58.3811, lng: -134.6420 },
  'Whale watch — wide-frame whale': { lat: 58.3811, lng: -134.6420 },
  'Whale watch — breach': { lat: 58.3811, lng: -134.6420 },

  // Endicott Arm — Stephens Passage
  'Endicott Arm — iceberg field': { lat: 57.7, lng: -132.9 },
  'Endicott Arm — Dawes Glacier face': { lat: 57.7, lng: -132.9 },
  'Endicott Arm — environmental landscape': { lat: 57.7, lng: -132.9 },

  // Mendenhall area — Lake + canoe + Steep Creek share lat/lng band
  'Mendenhall canoe — portraits in transit': { lat: 58.4147, lng: -134.5520 },
  'Mendenhall canoe — documentary': { lat: 58.4147, lng: -134.5520 },
  'Mendenhall Lake — glacier face from water': { lat: 58.4147, lng: -134.5520 },
  'Mendenhall Lake — wildlife at lake level': { lat: 58.4147, lng: -134.5520 },
  'Mendenhall Lake — iceberg composition': { lat: 58.4147, lng: -134.5520 },
  'Wildlife — Steep Creek bear vantage': { lat: 58.4147, lng: -134.5520 },

  // Specific Juneau locations
  'Landscape — Brotherhood Bridge': { lat: 58.395, lng: -134.58 },
  'Downtown — environmental portrait': { lat: 58.3019, lng: -134.4197 },

  // No specific location — score by time/player only
  'Wildlife — perched eagle': null,
  'Restaurant interior — group dinner': null,
  'Golden hour — landscape': null,
  'Departure — final frame': null,
};

function haversineKm(a, b) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

// For an excursion-anchored trigger, compute the ms timestamp we'd
// fire at: booking.date_iso + booking.start_local + offset_minutes
// (interpreted in the booking's tz). Returns null if the booking is
// missing / malformed.
export function excursionTriggerMs(excursion, offsetMinutes) {
  if (!excursion?.booking?.date_iso || !excursion.booking.start_local) return null;
  const tz = excursion.booking.tz || 'America/Juneau';
  const tzOffset = tzMinutesOffset(tz, excursion.booking.date_iso);
  const [h, m] = excursion.booking.start_local.split(':').map(Number);
  // Build a local-time Date by parsing as ISO with the resolved tz.
  const localISO = `${excursion.booking.date_iso}T${String(h).padStart(2, '0')}:${String(m || 0).padStart(2, '0')}:00`;
  // tzOffset minutes east of UTC; build a real timestamp.
  const utcMs = Date.parse(`${localISO}Z`) - tzOffset * 60_000;
  if (Number.isNaN(utcMs)) return null;
  return utcMs + (offsetMinutes || 0) * 60_000;
}

// Resolve a tz's offset (in minutes east of UTC) for a given date.
// AKDT (Alaska, DST) = -8h; AKST = -9h. Avoiding a tz library; we
// just need approximate offset for the trip dates.
function tzMinutesOffset(tz, dateIso) {
  // Proper resolution via Intl.DateTimeFormat with timeZoneName='shortOffset'.
  try {
    const d = new Date(`${dateIso}T12:00:00Z`);
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    });
    const parts = dtf.formatToParts(d);
    const tzn = parts.find((p) => p.type === 'timeZoneName')?.value || '';
    // 'GMT-08:00' or 'GMT-8' etc.
    const m = /GMT([+-])(\d{1,2})(?::(\d{2}))?/i.exec(tzn);
    if (!m) return -480; // default AK
    const sign = m[1] === '-' ? -1 : 1;
    const hh = parseInt(m[2], 10);
    const mm = m[3] ? parseInt(m[3], 10) : 0;
    return sign * (hh * 60 + mm);
  } catch {
    return -480; // AKDT fallback
  }
}

export function scoreScenario(scenario, ctx) {
  const { activePlayerId, now, coords, excursions, sunsetMs } = ctx;
  const nowMs = now instanceof Date ? now.getTime() : new Date(now).getTime();
  let score = 0;
  let triggerDeltaAbs = Infinity; // for tiebreaker

  // (1) Player match
  if (activePlayerId && scenario.player_id === activePlayerId) score += 50;

  // (2) Trigger time
  let triggerMs = null;
  if (scenario.trigger?.kind === 'excursion') {
    const ex = (excursions || []).find((e) => e.id === scenario.trigger.id);
    triggerMs = excursionTriggerMs(ex, scenario.trigger.offset_minutes);
  } else if (scenario.trigger?.kind === 'scheduled' && scenario.trigger.fire_at_iso) {
    const t = Date.parse(scenario.trigger.fire_at_iso);
    if (!Number.isNaN(t)) triggerMs = t;
  }
  if (triggerMs != null) {
    const deltaMins = Math.abs(triggerMs - nowMs) / 60000;
    triggerDeltaAbs = deltaMins;
    if (deltaMins <= 90) score += 100;
    else if (deltaMins <= 240) score += 30;
  }

  // (3) Golden hour boost (only for scenarios tagged Golden hour)
  if ((scenario.light_conditions || '').toLowerCase().includes('golden hour') && sunsetMs != null) {
    const minsUntilSunset = (sunsetMs - nowMs) / 60000;
    if (minsUntilSunset <= 60 && minsUntilSunset >= -15) score += 60;
  }

  // (4) Geo proximity
  if (coords?.lat != null && coords?.lng != null) {
    const target = ACTIVITY_COORDS[scenario.activity];
    if (target) {
      const km = haversineKm(coords, target);
      if (km < 5) score += 60;
      else if (km < 15) score += 20;
    }
  }

  return { score, triggerDeltaAbs, triggerMs };
}

export function rankScenarios(scenarios, ctx) {
  return scenarios
    .map((s) => ({ scenario: s, ...scoreScenario(s, ctx) }))
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      // Tiebreak: closer trigger wins; future > past at equal distance
      return a.triggerDeltaAbs - b.triggerDeltaAbs;
    });
}

// Convenience: top N scenarios with score > 0 (trim irrelevant
// zero-scored scenarios from "Right Now" — they belong in Browse).
export function topRightNow(scenarios, ctx, n = 3) {
  return rankScenarios(scenarios, ctx)
    .filter((r) => r.score > 0)
    .slice(0, n);
}

// Filter helpers for the Browse view.
export function distinctActivities(scenarios) {
  return Array.from(new Set(scenarios.map((s) => s.activity).filter(Boolean))).sort();
}

export function distinctLightConditions(scenarios) {
  return Array.from(
    new Set(scenarios.map((s) => s.light_conditions).filter(Boolean))
  ).sort();
}

export function distinctLenses(scenarios) {
  return Array.from(new Set(scenarios.map((s) => s.lens).filter(Boolean))).sort();
}
